import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AppButton from "./AppButton";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "tss-react/mui";
import { FormattedMessage } from "react-intl";

import { slugify } from "../services/utils";
import useDialogUrl from "../hooks/useDialogUrl";

interface ResponsiveDialogProps extends DialogProps {
  // Omit for the default Cancel/Submit pair; pass `null` when the content
  // renders its own footer and must not get a second one.
  actions?: React.ReactNode;
  classes?: any;
  // Add close Icon button or not
  crossIcon?: boolean;
  contentText?: string;
  onClose?: () => void;
  title: string;
  subtitle?: React.ReactNode;
  sx?: SxProps<Theme>;
  // Override the auto-generated URL slug (derived from title by default).
  // Set to false to disable URL sync for this dialog.
  dialogName?: string | false;
  // Entity identifier to include in the URL (?dialogId=<value>).
  dialogId?: string | number;
}

const useStyles = makeStyles()((theme) => ({
  title: {
    textAlign: "center",
  },
}));

export default function ResponsiveDialog({
  actions,
  classes,
  children,
  crossIcon = false,
  contentText,
  fullWidth,
  onClose,
  maxWidth,
  open,
  title,
  subtitle,
  sx,
  dialogName,
  dialogId,
  slotProps,
}: ResponsiveDialogProps) {
  const theme = useTheme();
  const { classes: baseClasses } = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const slug = dialogName === false ? null : slugify(dialogName || title);
  const idStr = dialogId != null ? String(dialogId) : null;
  const { effectiveOpen, handleClose } = useDialogUrl(
    slug,
    !!open,
    onClose,
    idStr
  );

  const [_actions, setActions] = useState(() =>
    actions !== undefined ? (
      actions
    ) : (
      <>
        <AppButton intent="secondary" onClick={handleClose}>
          <FormattedMessage id="CANCEL" />
        </AppButton>
        <AppButton intent="secondary" form="create-filter-form" type="submit">
          <FormattedMessage id="SUBMIT" />
        </AppButton>
      </>
    )
  );

  useEffect(() => {
    if (actions) {
      setActions(actions);
    }
  }, [actions]);

  return (
    <Dialog
      className={classes}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={effectiveOpen}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      aria-describedby="responsive-dialog-content"
      sx={sx}
      slotProps={slotProps}
    >
      <DialogTitle className={baseClasses.title} id="responsive-dialog-title">
        {title}
        {subtitle && (
          // A div, not a <p>: the subtitle may hold block content (chips).
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {subtitle}
          </Typography>
        )}
        {crossIcon && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 5,
              top: 5,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        {contentText && (
          <DialogContentText id="responsive-dialog-content">
            {contentText}
          </DialogContentText>
        )}
        {children}
      </DialogContent>
      {_actions !== null && <DialogActions>{_actions}</DialogActions>}
    </Dialog>
  );
}
